import * as DialogPrimitive from '@rn-primitives/dialog'
import type * as React from 'react'
import { Dimensions, Platform, StyleSheet, View, type ViewProps } from 'react-native'
import Animated, { FadeIn } from 'react-native-reanimated'
import Icon from '~/components/Icon'
import { cn } from '~/lib/utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

function DialogOverlayWeb({
  className,
  ...props
}: DialogPrimitive.OverlayProps & {
  ref?: React.RefObject<DialogPrimitive.OverlayRef>
}) {
  const { open } = DialogPrimitive.useRootContext()
  return (
    <DialogPrimitive.Overlay
      className={cn(
        'absolute inset-0 flex w-full flex-1 items-center justify-center bg-black/80 p-2',
        open ? 'web:animate-in web:fade-in-0' : 'web:animate-out web:fade-out-0',
        className
      )}
      {...props}
    />
  )
}

function DialogOverlayNative({
  className,
  children,
  ...props
}: DialogPrimitive.OverlayProps & {
  ref?: React.RefObject<DialogPrimitive.OverlayRef>
  children?: React.ReactNode
}) {
  return (
    <DialogPrimitive.Overlay
      style={StyleSheet.absoluteFill}
      className={cn('flex items-center justify-center bg-black/70 p-2', className)}
      {...props}>
      <Animated.View className="w-full flex-1" entering={FadeIn.duration(150)}>
        {children}
      </Animated.View>
    </DialogPrimitive.Overlay>
  )
}

const DialogOverlay = Platform.select({
  web: DialogOverlayWeb,
  default: DialogOverlayNative,
})

function DialogContent({
  className,
  children,
  portalHost,
  showCloseButton = true,
  ...props
}: DialogPrimitive.ContentProps & {
  ref?: React.RefObject<DialogPrimitive.ContentRef>
  className?: string
  portalHost?: string
  showCloseButton?: boolean
}) {
  const { open } = DialogPrimitive.useRootContext()
  const width = Dimensions.get('window').width

  return (
    <DialogPortal hostName={portalHost}>
      <DialogOverlay>
        <DialogPrimitive.Content
          className={cn(
            'web:cursor-default web:duration-200 relative my-auto w-full max-w-xl gap-4 rounded-content bg-background px-4 py-6 shadow-lg focus:outline-none focus:ring-0',
            open
              ? 'web:animate-in web:fade-in-0 web:zoom-in-95'
              : 'web:animate-out web:fade-out-0 web:zoom-out-95',
            className
          )}
          style={{
            minWidth: width >= 640 ? Math.min(504, width - 42) : 270,
            maxWidth: width,
          }}
          {...props}>
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              className={
                'web:group web:transition-opacity web:hover:opacity-100 web:focus:outline-none web:disabled:pointer-events-none absolute right-4 top-4 size-10 items-center justify-center self-center rounded-full border border-primary bg-transparent p-0'
              }>
              <Icon name="close" size={24} className={cn('text-secondary')} />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: ViewProps) {
  return (
    <View className={cn('flex flex-col gap-1.5 text-center sm:text-left', className)} {...props} />
  )
}

function DialogFooter({ className, ...props }: ViewProps) {
  return <View className={cn('flex flex-col gap-2', className)} {...props} />
}

function DialogTitle({
  className,
  ...props
}: DialogPrimitive.TitleProps & {
  ref?: React.RefObject<DialogPrimitive.TitleRef>
}) {
  return (
    <DialogPrimitive.Title
      className={cn(
        'native:text-xl font-semibold text-lg leading-none tracking-tight text-foreground',
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.DescriptionProps & {
  ref?: React.RefObject<DialogPrimitive.DescriptionRef>
}) {
  return (
    <DialogPrimitive.Description
      className={cn('native:text-base text-sm text-foreground', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
